import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import UrgencyBar from '@/components/feature/UrgencyBar';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import BathroomServices from './components/BathroomServices';
import BathroomProcess from './components/BathroomProcess';

const PRECISION_POINTS = [
  {
    icon: 'ri-drop-line',
    title: 'Water Goes Everywhere',
    desc: 'A missed waterproof membrane behind the tile means water reaches your subfloor and studs — silently — for months before you notice.',
  },
  {
    icon: 'ri-contrast-drop-line',
    title: 'Grout & Caulk Failures',
    desc: 'Rushed grout joints crack and lift. Bad caulk lines separate. Water finds every gap — and when it does, the repair costs far more than the original job.',
  },
  {
    icon: 'ri-ruler-2-line',
    title: 'Uneven Floors = Cracked Tile',
    desc: 'Tile laid on an unleveled surface will crack within a year. We fix the substrate before anything goes down — because the floor is the foundation.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Wrong Fixtures = Leaks',
    desc: 'Improperly set shower drains, toilets, or supply lines are ticking timers. We coordinate every fixture with the correct method and sealing standard.',
  },
];

const GALLERY = [
  {
    src: 'https://public.readdy.ai/ai/img_res/d2401268efa4f26e867a13c69c374dc1.jpg',
    label: 'Master Bath — Tampa',
    size: 'col-span-1 row-span-2',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=modern%20guest%20bathroom%20renovation%20with%20large%20grey%20concrete%20look%20tiles%2C%20floating%20vanity%20with%20vessel%20sink%2C%20backlit%20mirror%2C%20clean%20minimal%20design%2C%20professionally%20completed%20bathroom%20remodel%2C%20contemporary%20residential%20interior%20Florida&width=600&height=300&seq=bath_g2&orientation=landscape',
    label: 'Guest Bath — Sarasota',
    size: 'col-span-1',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=premium%20walk-in%20shower%20remodel%20with%20herringbone%20mosaic%20tile%20floor%2C%20large%20format%20wall%20tiles%2C%20built-in%20niche%20shelves%2C%20frameless%20glass%20door%2C%20polished%20chrome%20fixtures%2C%20beautiful%20finished%20shower%20renovation%20Tampa%20Bay&width=600&height=300&seq=bath_g3&orientation=landscape',
    label: 'Shower Remodel — St. Pete',
    size: 'col-span-1',
  },
];

export default function BathroomRemodelingPage() {
  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <UrgencyBar />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20luxury%20bathroom%20renovation%20result%20with%20floor%20to%20ceiling%20large%20format%20marble%20tiles%2C%20frameless%20glass%20walk-in%20shower%2C%20floating%20double%20vanity%20with%20warm%20LED%20mirror%20lighting%2C%20freestanding%20bathtub%2C%20moody%20elegant%20residential%20bathroom%20interior%2C%20premium%20high%20end%20remodel%20Tampa%20Bay&width=1920&height=1080&seq=bathhero001&orientation=landscape"
            alt="Bathroom remodeling Tampa Bay Sarasota"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/93 via-[#0d0d0d]/72 to-[#0d0d0d]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/60 via-transparent to-[#0d0d0d]/20" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#c9a55a]/15 border border-[#c9a55a]/30 rounded-full px-4 py-2 mb-8">
              <i className="ri-vip-crown-line text-[#c9a55a] text-sm" />
              <span className="text-[#c9a55a] text-sm font-semibold">High-Ticket Remodeling — Tampa Bay &amp; Sarasota</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
              Modern Bathroom Remodeling with{' '}
              <span className="text-[#c9a55a]">Clean Execution</span>
            </h1>

            <p className="text-white/65 text-xl font-light mb-4 leading-relaxed">
              This is not basic tile work.
            </p>
            <p className="text-white/50 text-base leading-relaxed mb-10 max-w-xl">
              We transform outdated bathrooms into clean, modern, and functional spaces —
              built with precision from structure to finish. Water, leveling, and
              finishing done correctly, every single time.
            </p>

            <div className="flex flex-wrap gap-6 mb-10">
              {[
                { value: '100%', label: 'Waterproofed' },
                { value: '0', label: 'Callbacks for Leaks' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col">
                  <span className="text-[#c9a55a] text-3xl font-black leading-none">{stat.value}</span>
                  <span className="text-white/45 text-xs mt-1 uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 bg-[#c9a55a] hover:bg-[#b8944a] text-[#0d0d0d] font-bold text-lg px-10 py-4 rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line" />
                Start Your Bathroom Project
              </Link>
              <a
                href={`https://wa.me/19044019383?text=${encodeURIComponent('Hi! I\'d like a free estimate for a bathroom remodel.\n\nProject scope: ___\nLocation: ___\nTimeline: ___')}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/40 text-[#25D366] font-semibold px-8 py-4 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg" />
                Quote via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Banner */}
      <section className="bg-[#111111] border-y border-white/8 py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 justify-between">
          <div className="max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
              Bathrooms require precision —{' '}
              <span className="text-[#c9a55a]">or problems appear.</span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed">
              Water, leveling, and finishing must be done correctly from the structure to the surface.
              We know where bathrooms fail — and we build to prevent every one of those failure points.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 flex-shrink-0">
            {['Tile Walls & Floors', 'Shower Remodels', 'Vanity Upgrades', 'Full Renovations'].map((item) => (
              <span
                key={item}
                className="bg-[#1a1a1a] border border-white/10 text-white/70 text-sm font-medium px-5 py-2.5 rounded-full whitespace-nowrap"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Bathroom Services */}
      <BathroomServices />

      {/* Why Precision Matters */}
      <section className="py-24 px-4 md:px-8 bg-[#1c1c1c] border-t border-white/8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Image */}
          <div className="relative rounded-2xl overflow-hidden h-[500px]">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20bathroom%20tile%20installer%20carefully%20applying%20waterproof%20membrane%20to%20shower%20walls%20before%20tiling%2C%20detailed%20close%20up%20showing%20proper%20waterproofing%20technique%20being%20applied%20with%20brush%20on%20concrete%20board%2C%20professional%20construction%20process%2C%20bright%20clean%20workspace&width=800&height=700&seq=bath_precision001&orientation=portrait"
              alt="Waterproofing bathroom before tile installation"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/50 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="bg-[#0d0d0d]/85 backdrop-blur-sm border border-white/10 rounded-xl px-5 py-4">
                <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-wider mb-1">The Step Most Crews Skip</p>
                <p className="text-white text-sm font-semibold">Waterproof membrane applied to every wet surface — walls, floor, niche, and transitions.</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">Why It Matters</p>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              Done wrong, a bathroom<br />
              <span className="text-[#c9a55a]">becomes a liability.</span>
            </h2>
            <p className="text-white/50 text-base leading-relaxed mb-10">
              A beautiful bathroom surface can hide serious structural problems.
              We don't just make it look good — we make sure what's behind the tile
              is built to handle water, humidity, and daily use for years.
            </p>

            <div className="flex flex-col gap-5">
              {PRECISION_POINTS.map((point) => (
                <div key={point.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#c9a55a]/10 border border-[#c9a55a]/20 rounded-xl flex-shrink-0">
                    <i className={`${point.icon} text-[#c9a55a] text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base mb-1">{point.title}</h3>
                    <p className="text-white/45 text-sm leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <BathroomProcess />

      {/* Gallery */}
      <section className="bg-[#111111] border-t border-white/8 py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-4">Our Work</p>
            <h2 className="text-4xl font-black text-white mb-4">Completed Projects</h2>
            <p className="text-white/45 text-sm max-w-md mx-auto">
              Real bathrooms, real results — serving homeowners across Tampa Bay &amp; Sarasota.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Large left */}
            <div className="relative group rounded-2xl overflow-hidden row-span-2 h-[440px] lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=stunning%20completed%20luxury%20master%20bathroom%20renovation%20with%20floor%20to%20ceiling%20white%20Carrara%20marble%20tiles%2C%20large%20walk%20in%20shower%20with%20frameless%20glass%2C%20freestanding%20soaking%20tub%2C%20double%20floating%20vanity%2C%20warm%20ambient%20lighting%2C%20high%20end%20residential%20Tampa%20Bay%20Florida%20remodel&width=600&height=700&seq=bath_g1b&orientation=portrait"
                alt="Master bathroom remodel Tampa Bay"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-white font-bold text-sm">Master Bath — Tampa</p>
                <p className="text-white/50 text-xs">Full renovation</p>
              </div>
            </div>

            {/* Top right */}
            <div className="relative group rounded-2xl overflow-hidden h-52">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20guest%20bathroom%20remodel%20with%20large%20format%20grey%20tiles%20floor%20and%20walls%2C%20wall-mounted%20floating%20vanity%20white%20oak%20finish%2C%20vessel%20sink%2C%20large%20backlit%20mirror%2C%20clean%20contemporary%20design%2C%20finished%20professional%20renovation%20Sarasota%20Florida&width=600&height=350&seq=bath_g2b&orientation=landscape"
                alt="Guest bathroom remodel Sarasota"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-bold text-sm">Guest Bath — Sarasota</p>
                <p className="text-white/50 text-xs">Vanity + tile upgrade</p>
              </div>
            </div>

            {/* Bottom right */}
            <div className="relative group rounded-2xl overflow-hidden h-52">
              <img
                src="https://readdy.ai/api/search-image?query=premium%20walk%20in%20shower%20renovation%20with%20herringbone%20marble%20mosaic%20tile%20floor%2C%20large%20subway%20tiles%20on%20walls%2C%20polished%20chrome%20rain%20shower%20head%2C%20built%20in%20tile%20shelf%20niche%2C%20frameless%20glass%20enclosure%2C%20beautifully%20finished%20St%20Petersburg%20Florida%20bathroom%20remodel&width=600&height=350&seq=bath_g3b&orientation=landscape"
                alt="Shower remodel St. Petersburg"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-bold text-sm">Shower Remodel — St. Pete</p>
                <p className="text-white/50 text-xs">Full shower rebuild</p>
              </div>
            </div>

            {/* Bottom full row */}
            <div className="relative group rounded-2xl overflow-hidden h-52 col-span-2 lg:col-span-3">
              <img
                src="https://readdy.ai/api/search-image?query=wide%20panoramic%20view%20of%20completed%20luxury%20bathroom%20renovation%20with%20continuous%20large%20format%20porcelain%20tile%20floor%20and%20walls%2C%20freestanding%20bathtub%20centered%20in%20space%2C%20double%20shower%20enclosure%2C%20warm%20natural%20light%20from%20skylight%2C%20premium%20complete%20bathroom%20transformation%20Tampa%20Bay%20Florida&width=1200&height=400&seq=bath_g4b&orientation=landscape"
                alt="Full bathroom transformation Tampa Bay"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-6">
                <p className="text-white font-bold text-base">Full Transformation — Tampa Bay</p>
                <p className="text-white/50 text-sm">Complete gut &amp; rebuild</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-24 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20dark%20moody%20luxury%20bathroom%20with%20backlit%20floating%20vanity%2C%20black%20marble%20tiles%2C%20ambient%20warm%20lighting%20from%20recessed%20ceiling%2C%20premium%20high%20end%20residential%20bathroom%20atmosphere%2C%20sophisticated%20dark%20interior%20design&width=1920&height=600&seq=bath_cta001&orientation=landscape"
            alt="Start your bathroom project"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#0d0d0d]/85 to-[#0d0d0d]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-[#c9a55a] text-xs font-bold uppercase tracking-[0.3em] mb-5">Ready to Transform Your Bathroom?</p>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
            Let&apos;s Build a Bathroom<br />
            <span className="text-[#c9a55a]">That Lasts.</span>
          </h2>
          <p className="text-white/50 text-base mb-10 leading-relaxed max-w-xl mx-auto">
            Free estimate, no obligation. Tell us about your project and we&apos;ll
            come see the space, discuss the scope, and give you a clear, honest quote.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#c9a55a] hover:bg-[#b8944a] text-[#0d0d0d] font-bold text-lg px-12 py-4 rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line" />
              Start Your Bathroom Project
            </Link>
            <a
              href="tel:+19044019383"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white font-semibold text-base transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-lg" />
              (904) 401-9383
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
